/**
 * CSS builder for the grid widget
 * Generates dynamic CSS based on widget settings
 */

export function buildCss(f) {
  if (!f || typeof f !== "object") return "";
  const SCOPE = `:is(#nexus-grid-widget, .nexus-grid-widget, [data-nexus-grid])`;
  const t = f.title || {};
  const pr = f.price || {};
  const rm = f.room || {};
  const loc = f.location || {};
  const it = f.item || {};
  const p = f.pagination || {};
  const tags = f.tags || {};
  const amenities = f.amenities || {};

  const cnt = f.count || {
    fontSize: 14,
    fontColor: "#222222",
    fontStyle: "normal",
    fontWeight: "600",
    fontFamily: "inherit",
    textAlign: "left",
    textTransform: "none",
  };

  // carousel vars (needed for bullets and arrows)
  const c = f.carousel || {
    bulletSize: 8,
    bulletGap: 6,
    bulletColor: "#ffffff",
    bulletBorderColor: "rgba(17,24,39,.25)",
    bulletActiveColor: "#111827",
    bulletActiveBorderColor: "#111827",
    bulletOpacity: 0.9,
    positionBottom: 8,
    arrowsBgColor: "rgba(0,0,0,.55)",
    arrowsIconColor: "#ffffff",
    arrowsIconSize: 18,
    arrowsSize: 34,
    arrowsPadding: 8,
    arrowsTopOffset: 0,
  };

  const bw = it.borderWidth
    ? `${it.borderWidth.top || 0}px ${it.borderWidth.right || 0}px ${it.borderWidth.bottom || 0}px ${it.borderWidth.left || 0}px`
    : "1px";
  const jc = (p.align || "center") === "left" ? "flex-start" : (p.align || "center") === "right" ? "flex-end" : "center";

  const w = f.widget || {};
  const h = w.height || {};
  const widgetHeight =
    Number.isFinite(h.value) && (h.unit === "vh" || h.unit === "%")
      ? `${h.value}${h.unit}`
      : null;

  return `
  /* ====== CSS Variables for Carousel (allows ListGrid base CSS to read these) ====== */
  ${SCOPE}{
    --nxw-arrow-bg:${c.arrowsBgColor || "rgba(0,0,0,.55)"};
    --nxw-arrow-color:${c.arrowsIconColor || "#ffffff"};
    --nxw-arrow-icon-size:${c.arrowsIconSize || 18}px;
    --nxw-arrow-size:${c.arrowsSize || 34}px;
    --nxw-arrow-padding:${c.arrowsPadding || 8}px;
    --nxw-arrow-top-offset:${c.arrowsTopOffset || 0}px;
    --nxw-bullet-size:${c.bulletSize || 8}px;
    --nxw-bullet-gap:${Math.max(0, Math.round((c.bulletGap||6)/2))}px;
    --nxw-bullet-color:${c.bulletColor || "#ffffff"};
    --nxw-bullet-border-color:${c.bulletBorderColor || "rgba(17,24,39,.25)"};
    --nxw-bullet-active-color:${c.bulletActiveColor || "#111827"};
    --nxw-bullet-active-border-color:${c.bulletActiveBorderColor || "#111827"};
    --nxw-bullet-opacity:${c.bulletOpacity ?? 0.9};
    --nxw-bullet-bottom:${c.positionBottom || 8}px;
    --nxw-body-padding:${it.padding || 16}px;
    --nxw-body-gap:${it.contentGap ?? 8}px;
  }

  /* SWIPER ARROWS: styling from saved settings */
  ${SCOPE} .nxw-swiper .swiper-button-prev,
  ${SCOPE} .nxw-swiper .swiper-button-next{
    background:${c.arrowsBgColor || "rgba(0,0,0,.55)"}!important;
    color:${c.arrowsIconColor || "#ffffff"}!important;
    width:${c.arrowsSize || 34}px!important;
    height:${c.arrowsSize || 34}px!important;
    padding:${c.arrowsPadding || 8}px!important;
    border-radius:9999px!important;
    top:calc(50% + ${c.arrowsTopOffset || 0}px)!important;
  }
  /* Swiper uses ::after for the icon glyph (default) */
  ${SCOPE} .nxw-swiper .swiper-button-prev::after,
  ${SCOPE} .nxw-swiper .swiper-button-next::after{
    font-size:${c.arrowsIconSize || 18}px!important;
    color:${c.arrowsIconColor || "#ffffff"}!important;
    line-height:1!important;
  }
  /* Custom SVG navigation icons */
  ${SCOPE} .nxw-swiper .swiper-button-prev .swiper-navigation-icon,
  ${SCOPE} .nxw-swiper .swiper-button-next .swiper-navigation-icon,
  ${SCOPE} .nxw-swiper .swiper-button-prev svg,
  ${SCOPE} .nxw-swiper .swiper-button-next svg{
    width:${c.arrowsIconSize || 18}px!important;
    height:${c.arrowsIconSize || 18}px!important;
    color:${c.arrowsIconColor || "#ffffff"}!important;
    fill:currentColor!important;
  }

  /* SWIPER NAV: hidden by default, show on card hover/focus */
  ${SCOPE} .nxw-card .nxw-swiper .swiper-button-prev,
  ${SCOPE} .nxw-card .nxw-swiper .swiper-button-next{
    opacity:0!important;
    pointer-events:none!important;
    transition:opacity .15s ease;
  }
  ${SCOPE} .nxw-card:hover .nxw-swiper .swiper-button-prev,
  ${SCOPE} .nxw-card:hover .nxw-swiper .swiper-button-next,
  ${SCOPE} .nxw-card:focus-within .nxw-swiper .swiper-button-prev,
  ${SCOPE} .nxw-card:focus-within .nxw-swiper .swiper-button-next{
    opacity:1!important;
    pointer-events:auto!important;
  }

  /* Desktop: Hide map when data-map-state="false" */
  ${SCOPE}[data-map-state="false"] .ww-map,
  ${SCOPE}[data-map-state="false"] .nxm-wrap,
  ${SCOPE}[data-map-state="false"] .nxm-map,
  ${SCOPE}[data-map-state="false"] .nxm-google-map-wrap,
  ${SCOPE}[data-map-state="false"] .nxm-google-map,
  ${SCOPE}[data-map-state="false"] .leaflet-container{display:none!important;visibility:hidden!important;pointer-events:none!important;}
  ${SCOPE}[data-map-state="false"] .ww-responsive{height:auto!important;}
  ${SCOPE}[data-map-state="false"] .ww-list{flex:1 1 auto!important;min-width:0!important;height:auto!important;overflow:visible!important;}

  /* Mobile/Tablet: Hide map when data-map-state-mobile="false" (max-width: 1023px) */
  @media (max-width: 1023px) {
    ${SCOPE}[data-map-state-mobile="false"] .ww-map,
    ${SCOPE}[data-map-state-mobile="false"] .nxm-wrap,
    ${SCOPE}[data-map-state-mobile="false"] .nxm-map,
    ${SCOPE}[data-map-state-mobile="false"] .nxm-google-map-wrap,
    ${SCOPE}[data-map-state-mobile="false"] .nxm-google-map,
    ${SCOPE}[data-map-state-mobile="false"] .leaflet-container{display:none!important;visibility:hidden!important;pointer-events:none!important;}
    ${SCOPE}[data-map-state-mobile="false"] .ww-responsive{height:auto!important;}
    ${SCOPE}[data-map-state-mobile="false"] .ww-list{flex:1 1 auto!important;min-width:0!important;height:auto!important;overflow:visible!important;}
  }

  /* MAP CONTROL BUTTONS - floating on top right of map */
  ${SCOPE} .nxw-map-controls{
    position:absolute!important;
    top:10px!important;
    right:10px!important;
    z-index:10000!important;
    display:flex!important;
    flex-direction:column!important;
    gap:6px!important;
    pointer-events:auto!important;
  }
  ${SCOPE} .nxw-map-ctrl-btn{
    width:40px!important;
    height:40px!important;
    min-width:40px!important;
    min-height:40px!important;
    border-radius:50%!important;
    background:#ffffff!important;
    border:none!important;
    box-shadow:0 0 0 2px rgba(0,0,0,.1), 0 2px 4px rgba(0,0,0,.2)!important;
    cursor:pointer!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    color:#333!important;
    transition:background .15s, box-shadow .15s!important;
    padding:0!important;
    margin:0!important;
    outline:none!important;
    -webkit-appearance:none!important;
    appearance:none!important;
  }
  ${SCOPE} .nxw-map-ctrl-btn:hover{
    background:#f0f0f0!important;
  }
  ${SCOPE} .nxw-map-ctrl-btn:focus{
    box-shadow:0 0 0 2px rgba(0,0,0,.1), 0 2px 4px rgba(0,0,0,.2), 0 0 0 3px rgba(66,133,244,.4)!important;
  }
  ${SCOPE} .nxw-map-ctrl-btn svg{
    width:20px!important;
    height:20px!important;
    stroke-width:2!important;
    flex-shrink:0!important;
  }
  /* Ensure nxm-wrap doesn't block buttons */
  ${SCOPE} .ww-map .nxm-wrap{z-index:1!important;}
  ${SCOPE} .ww-map .leaflet-control-container{z-index:400!important;}

  /* MAP FULLSCREEN MODE - fills widget area, hides listings */
  ${SCOPE} .ww-map-expanded,
  ${SCOPE} .ww-responsive.ww-map-expanded{
    display:block!important;
    flex-direction:column!important;
  }
  ${SCOPE} .ww-map-expanded .ww-list,
  ${SCOPE} .ww-responsive.ww-map-expanded .ww-list{
    display:none!important;
  }
  ${SCOPE} .ww-map-expanded .ww-map,
  ${SCOPE} .ww-responsive.ww-map-expanded .ww-map{
    width:100%!important;
    height:80vh!important;
    max-height:none!important;
    flex:1 1 100%!important;
    position:relative!important;
    order:1!important;
  }
  ${SCOPE} .ww-map-expanded .ww-map .nxm-wrap,
  ${SCOPE} .ww-map-expanded .ww-map .nxm-map,
  ${SCOPE} .ww-map-expanded .ww-map .leaflet-container,
  ${SCOPE} .ww-map-expanded .ww-map .nxm-google-map{
    height:80vh!important;
    width:100%!important;
  }

  /* SHOW MAP BUTTON (when map is hidden) */
  ${SCOPE} .nxw-show-map-btn{
    display:inline-flex;
    align-items:center;
    gap:6px;
    padding:8px 16px;
    background:#fff;
    border:1px solid #d1d5db;
    border-radius:8px;
    font-size:14px;
    font-weight:500;
    color:#374151;
    cursor:pointer;
    box-shadow:0 1px 3px rgba(0,0,0,.1);
    transition:all .15s ease;
  }
  ${SCOPE} .nxw-show-map-btn:hover{
    background:#f9fafb;
    border-color:#9ca3af;
  }
  ${SCOPE} .nxw-show-map-btn svg{
    width:16px;
    height:16px;
  }

  /* SWIPER BULLETS (from saved style) */
  ${SCOPE} .nxw-swiper .swiper-pagination{bottom:${c.positionBottom}px!important;}
  ${SCOPE} .nxw-swiper .swiper-pagination-bullets .swiper-pagination-bullet{
    width:${c.bulletSize}px!important;height:${c.bulletSize}px!important;
    opacity:${c.bulletOpacity}!important;background:${c.bulletColor}!important;
    border:1px solid ${c.bulletBorderColor}!important;
    margin:0 ${Math.max(0, Math.round((c.bulletGap||0)/2))}px!important;
    border-radius:999px!important;
  }
  ${SCOPE} .nxw-swiper .swiper-pagination-bullets .swiper-pagination-bullet-active{
    background:${c.bulletActiveColor}!important;border-color:${c.bulletActiveBorderColor}!important;
  }

  ${t.ellipsis ? `
  /* Single-line truncation for titles */
  ${SCOPE} .nxw-title{
    display:block !important;
    width:100% !important;
    max-width:100% !important;
    white-space:nowrap !important;
    overflow:hidden !important;
    text-overflow:ellipsis !important;
  }` : ``}

  ${SCOPE}{
    --nxw-title-fs:${t.fontSize||17}px;
    --nxw-title-color:${t.fontColor||"#111827"};
    --nxw-title-style:${t.fontStyle||"normal"};
    --nxw-title-fw:${t.fontWeight||600};
    --nxw-title-family:${t.fontFamily||"inherit"};
    --nxw-loc-fs:${loc.fontSize||17}px;
    --nxw-loc-color:${loc.fontColor||"#111827"};
    --nxw-loc-style:${loc.fontStyle||"normal"};
    --nxw-loc-fw:${loc.fontWeight||600};
    --nxw-loc-family:${loc.fontFamily||"inherit"};
    --nxw-price-fs:${pr.fontSize||17}px;
    --nxw-price-color:${pr.fontColor||"#111827"};
    --nxw-price-style:${pr.fontStyle||"normal"};
    --nxw-price-fw:${pr.fontWeight||600};
    --nxw-price-family:${pr.fontFamily||"inherit"};
    --nxw-meta-fs:${rm.fontSize||14}px;
    --nxw-meta-color:${rm.fontColor||"#111827"};
    --nxw-meta-style:${rm.fontStyle||"normal"};
    --nxw-meta-fw:${rm.fontWeight||600};
    --nxw-meta-family:${rm.fontFamily||"inherit"};
    --nxw-count-fs:${cnt.fontSize||14}px;
    --nxw-count-color:${cnt.fontColor||"#222222"};
    --nxw-count-style:${cnt.fontStyle||"normal"};
    --nxw-count-fw:${cnt.fontWeight||600};
    --nxw-count-family:${cnt.fontFamily||"inherit"};
    --nxw-count-align:${cnt.textAlign||"left"};
    --nxw-count-transform:${cnt.textTransform||"none"};
    --nxw-tag-bg:${tags.bgColor||"#ffffff"};
    --nxw-tag-color:${tags.textColor||"#374151"};
    --nxw-tag-fs:${tags.fontSize||12}px;
    --nxw-tag-fw:${tags.fontWeight||500};
    --nxw-tag-radius:${tags.borderRadius||100}px;
    --nxw-tag-bc:${tags.borderColor||"rgba(17,24,39,0.15)"};
    --nxw-tags-gap:${tags.gap||6}px;
    --nxw-amenity-bg:${amenities.bgColor||"rgba(255,255,255,0.95)"};
    --nxw-amenity-color:${amenities.textColor||"#111827"};
    --nxw-amenity-fs:${amenities.fontSize||12}px;
    --nxw-amenity-fw:${amenities.fontWeight||600};
    --nxw-amenity-radius:${amenities.borderRadius||6}px;
    --nxw-amenity-bc:${amenities.borderColor||"rgba(17,24,39,0.1)"};
  }
  ${SCOPE} .nxw-card{border-style:${it.borderStyle||"solid"};border-color:${it.borderColor||"#e5e7eb"};border-width:${bw};transition:box-shadow .2s ease,transform .2s ease;}
  ${SCOPE} .nxw-card:hover{box-shadow:0 8px 30px rgba(2,6,23,.15)!important;}
  ${SCOPE} .nxw-media{overflow:hidden;${it.imageMinHeight ? `min-height:${it.imageMinHeight}px;` : ''}}
  ${SCOPE} .nxw-media::before{padding-top:${it.imageHeight||220}px;${it.imageMinHeight ? `min-height:${it.imageMinHeight}px;` : ''}}
  ${it.imageHoverGrow !== false ? `
  ${SCOPE} .nxw-media .nxw-img,
  ${SCOPE} .nxw-media .nxw-media-fill,
  ${SCOPE} .nxw-media .nxw-swiper,
  ${SCOPE} .nxw-media .swiper-slide img{transition:transform .3s ease;}
  ${SCOPE} .nxw-card:hover .nxw-media .nxw-img,
  ${SCOPE} .nxw-card:hover .nxw-media .nxw-media-fill,
  ${SCOPE} .nxw-card:hover .nxw-media .nxw-swiper,
  ${SCOPE} .nxw-card:hover .nxw-media .swiper-slide img{transform:scale(1.05);}` : ''}
  ${SCOPE} .nxw-body{min-height:${it.contentHeight||140}px;}
  ${SCOPE} .nxp-wrap{display:flex;justify-content:${jc};align-items:center;gap:${p.gap||8}px;font-size:${p.fontSize||14}px;font-weight:${p.fontWeight||600};font-family:${p.fontFamily||"inherit"};}
  ${SCOPE} .nxp-btn{font-size:${p.fontSize||14}px;height:${p.size||36}px;min-width:${p.size||36}px;padding:${p.paddingY||8}px ${p.paddingX||10}px;border-radius:${p.radius||10}px;border:${p.borderWidth||1}px ${p.borderStyle||"solid"} ${p.borderColor||"#e5e7eb"};color:${p.textColor||"#111827"};background:${p.bgColor||"#fff"};}
  ${SCOPE} .nxp-btn:hover:not([disabled]){color:${p.hover?.textColor||"#111827"};background:${p.hover?.bgColor||"#f3f4f6"};border-color:${p.hover?.borderColor||"#d1d5db"};}
  ${SCOPE} .nxp-btn--active{color:${p.active?.textColor||"#fff"};background:${p.active?.bgColor||"#111827"};border-color:${p.active?.borderColor||"#111827"};}
  ${SCOPE} .nxp-btn[disabled]{color:${p.disabled?.textColor||"#9ca3af"};background:${p.disabled?.bgColor||"#f9fafb"};border-color:${p.disabled?.borderColor||"#e5e7eb"};}
`.trim();
}
